// src/padplanner/CommentPins.js
//
// A revision's comment pins on the plan page's map (S6a2, mockup 5): an
// orange disc with the pin number at each comment's place, gray once
// resolved, ringed when selected. DOM buttons in an overlay over the map, as
// the editor's labels are, so a pin is a click target and reads its number
// without a glyph font. Placed again on every map move.
//
// A comment's place is a Point, a LineString or a Polygon (S6a1). Its pin
// sits at the point, the middle vertex of a line, or the first vertex of a
// ring: where the comment was made, not a computed center.
const pinAt = (g) => {
  if (!g?.coordinates) return null;
  if (g.type === "Point") return g.coordinates;
  if (g.type === "LineString") return g.coordinates[Math.floor(g.coordinates.length / 2)] ?? null;
  if (g.type === "Polygon") return g.coordinates[0]?.[0] ?? null;
  return null;
};

export default class CommentPins {
  constructor({ map, onPick = null }) {
    this.map = map;
    this.onPick = onPick;
    this.comments = [];
    this.selectedId = null;
    this.visible = true;
    this.pending = null;
    const container = map.getContainer?.();
    if (container) {
      const el = document.createElement("div");
      el.setAttribute("data-testid", "pp-comment-pins");
      Object.assign(el.style, { position: "absolute", inset: "0", pointerEvents: "none", overflow: "hidden" });
      container.appendChild(el);
      this.el = el;
    }
    this._onMove = () => this._place();
    map.on("move", this._onMove);
  }

  destroy() {
    this.map.off?.("move", this._onMove);
    this.el?.remove();
    this.el = null;
  }

  /** comments: GET …/comments' list. */
  set(comments, { selectedId = this.selectedId } = {}) {
    this.comments = comments ?? [];
    this.selectedId = selectedId;
    this._render();
  }

  select(id) { this.selectedId = id; this._render(); }
  setVisible(on) { this.visible = Boolean(on); this._render(); }
  /** The pin a comment being written will have, before it is saved: [lng, lat] or null. */
  setPending(lngLat) { this.pending = lngLat; this._render(); }

  _render() {
    const el = this.el;
    if (!el) return;
    el.replaceChildren();
    if (!this.visible) return;
    for (const c of this.comments) {
      const at = pinAt(c.geometry);
      if (!at) continue;
      const b = document.createElement("button");
      b.type = "button";
      b.className = "pp-pin";
      b.dataset.commentId = c.id;
      b.dataset.resolved = c.resolved ? "true" : "false";
      b.textContent = String(c.pin_no);
      b.setAttribute("aria-label", `Comment ${c.pin_no}${c.resolved ? " (resolved)" : ""}`);
      const selected = c.id === this.selectedId;
      Object.assign(b.style, {
        position: "absolute", left: "0", top: "0", width: "22px", height: "22px", borderRadius: "9999px",
        pointerEvents: "auto", cursor: "pointer", font: "700 11px/22px ui-sans-serif, system-ui, sans-serif", textAlign: "center",
        color: c.resolved ? "#e2e8f0" : "#111827", background: c.resolved ? "#64748b" : "#f97316",
        border: selected ? "2px solid #22d3ee" : "2px solid #fff7ed", padding: "0",
      });
      b._lngLat = at;
      b.addEventListener("click", (e) => { e.stopPropagation(); this.onPick?.(c.id); });
      el.appendChild(b);
    }
    if (this.pending) {
      const p = document.createElement("span");
      p.dataset.pending = "true";
      p.textContent = "+";
      Object.assign(p.style, {
        position: "absolute", left: "0", top: "0", width: "22px", height: "22px", borderRadius: "9999px",
        font: "700 13px/20px ui-sans-serif, system-ui, sans-serif", textAlign: "center", color: "#f97316",
        background: "rgba(15, 23, 42, 0.85)", border: "2px dashed #f97316",
      });
      p._lngLat = this.pending;
      el.appendChild(p);
    }
    this._place();
  }

  _place() {
    if (!this.el) return;
    for (const n of this.el.children) {
      const p = this.map.project(n._lngLat);
      n.style.transform = `translate(${p.x}px, ${p.y}px) translate(-50%, -50%)`;
    }
  }

  /** What the pins say now, for tests: [{id, pin, resolved, selected}]. */
  shown() {
    return [...(this.el?.querySelectorAll(".pp-pin") ?? [])].map((b) => ({
      id: b.dataset.commentId, pin: Number(b.textContent), resolved: b.dataset.resolved === "true",
      selected: b.style.border.includes("#22d3ee") || b.style.border.includes("34, 211, 238"),
    }));
  }
}
